import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PlantIcon, SpinnerGapIcon, ChartLineUpIcon, ArrowBendUpRightIcon, ShareNetworkIcon, StarIcon } from "@phosphor-icons/react";
import { fetchOrganicRunners } from "../lib/api";
import { fmtUsd } from "../lib/format";
import { QuickBuyLink } from "../components/TradeButton";
import { RunnerShareCard } from "../components/RunnerShareCard";
import { useWatchlist } from "../context/WatchlistContext";
import { useSeo } from "../lib/useSeo";

const NEON = "#4ADE80";
const GOLD = "#F0C000";

const RunnerRow = ({ r, rank, onShare }) => {
  const { isWatched, toggle } = useWatchlist();
  const watched = isWatched(r.mint);
  const ansem = (r.conviction ?? 0) >= 75;
  return (
    <div
      data-testid={`organic-runner-row-${r.mint}`}
      className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: ansem ? `${GOLD}66` : `${NEON}33`, background: ansem ? `linear-gradient(90deg, ${GOLD}12, transparent)` : rank <= 3 ? `linear-gradient(90deg, ${NEON}10, transparent)` : "transparent" }}
    >
      <div className="w-7 text-center font-heading font-black text-lg shrink-0" style={{ color: rank <= 3 ? NEON : "#666" }}>
        {rank}
      </div>
      {r.image ? (
        <img src={r.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
      ) : (
        <div className="w-9 h-9 flex items-center justify-center border shrink-0" style={{ borderColor: `${NEON}55`, color: NEON }}>
          <PlantIcon size={18} weight="bold" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/coin/${r.mint}`} className="font-heading font-bold hover:text-neon transition-colors">${r.symbol || "token"}</Link>
          <span data-testid={`runner-conviction-${r.mint}`} title="Credibility-blind conviction score — Volume + Survival + Buy Pressure only" className="font-mono text-[10px] px-1.5 py-0.5 tracking-wide font-bold" style={ansem ? { background: `${GOLD}22`, border: `1px solid ${GOLD}`, color: GOLD } : { background: `${NEON}18`, border: `1px solid ${NEON}66`, color: NEON }}>
            {ansem ? "👀 " : ""}{r.conviction}/100
          </span>
          {ansem && (
            <span title="ANSEM-tier: elite volume/survival even if token info looks weak" className="font-mono text-[10px] px-1.5 py-0.5 font-bold animate-pulse" style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}`, color: GOLD }}>
              ANSEM-TIER
            </span>
          )}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground truncate mt-0.5">
          Dipped <span className="text-danger">-{r.dipPct}%</span> from peak · bouncing <span style={{ color: NEON }}>{r.bounce}×</span> off the low
          {r.ageHours != null ? ` · ${Math.round(r.ageHours)}h old` : ""}
          {r.changeH1 != null ? <> · <span style={{ color: r.changeH1 >= 0 ? NEON : "#FF3366" }}>{r.changeH1 >= 0 ? "+" : ""}{Math.round(r.changeH1)}% 1h</span></> : null}
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="font-mono text-sm font-bold">{fmtUsd(r.marketCap)}</div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">mcap · {fmtUsd(r.liquidity)} liq</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <QuickBuyLink coin={{ address: r.mint, url: r.url }} testid={`runner-buy-${r.mint}`} />
        <button onClick={() => toggle(r.mint)} title={watched ? "Remove from watchlist" : "Save to watchlist — track its 2nd wind"} data-testid={`runner-watch-btn-${r.mint}`} className="p-1.5 transition-colors hover:bg-white/5">
          <StarIcon size={16} weight={watched ? "fill" : "regular"} color={watched ? "#F5A623" : "#666"} />
        </button>
        <button onClick={() => onShare(r)} title="Share this runner call" data-testid={`runner-share-btn-${r.mint}`} className="p-1.5 text-muted-foreground hover:text-neon transition-colors">
          <ShareNetworkIcon size={16} weight="bold" />
        </button>
        <Link to={`/coin/${r.mint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`runner-chart-${r.mint}`}>
          <ChartLineUpIcon size={16} weight="bold" />
        </Link>
      </div>
    </div>
  );
};

export default function OrganicRunners() {
  const [shareRunner, setShareRunner] = useState(null);
  useSeo({
    title: "🌱 Organic Runners — Survivor-then-Run Meme Coins | AlphaApeing",
    description: "Coins that shook out hard from their peak and are bouncing back with real buyers — ranked by a credibility-blind conviction score.",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["organicRunners"],
    queryFn: fetchOrganicRunners,
    refetchInterval: 60000,
  });
  const runners = data?.runners || [];

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="organic-runners-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: NEON }}>
            <PlantIcon size={24} weight="bold" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            ORGANIC <span style={{ color: NEON }}>RUNNERS</span>
          </h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          The <span style={{ color: NEON }} className="font-semibold">survivor-then-run pattern</span> — coins that dumped hard from their peak,
          got left for dead, then came back with real buyers and liquidity. Ranked by a{" "}
          <span style={{ color: GOLD }} className="font-semibold">credibility-blind conviction score</span> (volume + survival + buy pressure only) so
          hidden gems surface even when their token info looks weak. <span style={{ color: GOLD }}>👀 ANSEM-tier</span> = conviction ≥ 75.
        </p>
      </div>

      <Link to="/" data-testid="organic-runners-back" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-neon transition-colors">
        <ArrowBendUpRightIcon size={14} className="rotate-180" /> Back to the Radar
      </Link>

      {isLoading ? (
        <div className="flex justify-center py-20"><SpinnerGapIcon size={32} className="animate-spin" style={{ color: NEON }} /></div>
      ) : runners.length === 0 ? (
        <div className="border border-white/10 bg-card py-20 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-3">
          <PlantIcon size={30} style={{ color: NEON }} />
          No organic runners right now. A coin lands here when it dips ≥35% from its peak and then bounces 1.4×+ with buyers coming back.
          <Link to="/" className="text-neon hover:underline">Browse the Radar →</Link>
        </div>
      ) : (
        <div className="border border-white/5 divide-y divide-white/5" data-testid="organic-runners-rows">
          {runners.map((r, i) => <RunnerRow key={r.mint} r={r} rank={i + 1} onShare={setShareRunner} />)}
        </div>
      )}

      <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <PlantIcon size={13} style={{ color: NEON }} />
        Ranked by conviction then bounce strength. Community-flagged scams & copycat tickers auto-hidden. Not financial advice.
      </div>

      {shareRunner && <RunnerShareCard runner={shareRunner} open={!!shareRunner} onClose={() => setShareRunner(null)} />}
    </div>
  );
}
